import { useState, useRef, useEffect } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useScannerContext } from "@/contexts/ScannerContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tag, Plus, Edit, Trash2, MapPin, Package, AlertCircle, CheckCircle, XCircle, Printer, Radio, Scan, Loader2, Wifi, Users } from "lucide-react";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { toast } from "sonner";
import { useReactToPrint } from "react-to-print";
import { PrintableNFCLabel } from "@/components/NFC/PrintableNFCLabel";
import { useNFCReader } from "@/hooks/useNFCReader";
import { BarcodeInput } from "@/components/Barcode/BarcodeInput";

const NFCTagsAdmin = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [printingTag, setPrintingTag] = useState<any>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const { isSupported, isScanning, isWriting, writeToTag, scanTagUid, writeFullTag, lastRead: mobileLastRead } = useNFCReader();
  const [scanningForUid, setScanningForUid] = useState(false);
  const [lastProcessedTimestamp, setLastProcessedTimestamp] = useState<number>(0);
  const { nfcBridge, writeTag: writeTagUsb } = useScannerContext();

  // NFC disponible si hay soporte móvil O lector USB conectado
  const nfcAvailable = isSupported || nfcBridge.isReaderReady;

  const [formData, setFormData] = useState({
    tag_uid: "",
    tag_label: "",
    process_id: "",
    work_order_id: "",
    assigned_to_mold_id: "",
    assigned_to_user_id: "",
    location: "",
    status: "active",
    notes: "",
  });

  // Detectar lectura de tag NFC desde el lector USB (via ScannerContext)
  useEffect(() => {
    if (nfcBridge.lastRead && isDialogOpen && !editingTag) {
      setFormData(prev => ({ ...prev, tag_uid: nfcBridge.lastRead!.uid }));
      toast.success(`UID detectado (USB): ${nfcBridge.lastRead.uid}`);
    }
  }, [nfcBridge.lastRead, isDialogOpen, editingTag]);

  // Detectar lectura de tag NFC desde móvil (via useNFCReader)
  useEffect(() => {
    if (mobileLastRead && isDialogOpen && !editingTag && mobileLastRead.timestamp > lastProcessedTimestamp) {
      setFormData(prev => ({ ...prev, tag_uid: mobileLastRead.serialNumber }));
      setLastProcessedTimestamp(mobileLastRead.timestamp);
      toast.success(`UID detectado (Móvil): ${mobileLastRead.serialNumber}`);
    }
  }, [mobileLastRead, isDialogOpen, editingTag, lastProcessedTimestamp]);

  // Al abrir el dialog, verificar si hay una lectura reciente del USB
  useEffect(() => {
    if (isDialogOpen && !editingTag && nfcBridge.lastRead && !formData.tag_uid) {
      // Verificar si la lectura es reciente (menos de 30 segundos)
      const isRecent = Date.now() - nfcBridge.lastRead.timestamp < 30000;
      if (isRecent) {
        setFormData(prev => ({ ...prev, tag_uid: nfcBridge.lastRead!.uid }));
        toast.success(`UID reciente detectado: ${nfcBridge.lastRead.uid}`);
      }
    }
  }, [isDialogOpen, editingTag]);

  // Al abrir el dialog, verificar si hay una lectura reciente del MÓVIL
  useEffect(() => {
    if (isDialogOpen && !editingTag && mobileLastRead && !formData.tag_uid) {
      const isRecent = Date.now() - mobileLastRead.timestamp < 30000;
      if (isRecent) {
        setFormData(prev => ({ ...prev, tag_uid: mobileLastRead.serialNumber }));
        setLastProcessedTimestamp(mobileLastRead.timestamp);
        toast.success(`UID reciente detectado (Móvil): ${mobileLastRead.serialNumber}`);
      }
    }
  }, [isDialogOpen, editingTag, mobileLastRead]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `NFC-${printingTag?.tag_uid || 'tag'}`,
    onAfterPrint: () => setPrintingTag(null),
  });

  const printLabel = (tag: any) => {
    setPrintingTag(tag);
    // Trigger print after state update
    setTimeout(() => {
      handlePrint();
    }, 100);
  };

  // Fetch NFC tags
  const { data: tags, isLoading } = useQuery({
    queryKey: ['nfc-tags', filterStatus],
    queryFn: async () => {
      let query = api
        .from('nfc_tags')
        .select(`
          *,
          manufacturing_processes:process_id (
            id,
            name,
            description
          ),
          work_orders:work_order_id (
            id,
            order_number,
            product_name,
            status
          ),
          molds:assigned_to_mold_id (
            id,
            name,
            code
          ),
          user_profile:assigned_to_user_id (
            id,
            full_name,
            employee_id
          )
        `)
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      // Fetch scanned_by profiles separately to avoid relation errors
      if (data) {
        const userIds = data.map(tag => tag.last_scanned_by).filter(Boolean);
        if (userIds.length > 0) {
          const { data: profiles } = await api
            .from('profiles')
            .select('id, full_name')
            .in('id', userIds);
          
          // Map profiles to tags
          return data.map(tag => ({
            ...tag,
            scanned_by_profile: profiles?.find(p => p.id === tag.last_scanned_by)
          }));
        }
      }
      
      return data;
    },
    enabled: !!user,
  });

  // Fetch work orders for dropdown
  const { data: workOrders } = useQuery({
    queryKey: ['work-orders-list'],
    queryFn: async () => {
      const { data, error } = await api
        .from('work_orders')
        .select('id, order_number, product_name')
        .in('status', ['pending', 'in_progress'])
        .order('order_number');
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Fetch processes for dropdown
  const { data: processes } = useQuery({
    queryKey: ['processes-list'],
    queryFn: async () => {
      const { data, error } = await api
        .from('manufacturing_processes')
        .select('id, name, sequence_order')
        .order('sequence_order');
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: molds } = useQuery({
    queryKey: ['molds-list'],
    queryFn: async () => {
      const { data, error } = await api
        .from('molds')
        .select('id,name,code')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: usersForAttendance } = useQuery({
    queryKey: ['profiles-for-nfc'],
    queryFn: async () => {
      const { data, error } = await api
        .from('profiles')
        .select('id, full_name, employee_id')
        .eq('is_active', true)
        .order('full_name');
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      const hasAssignment = Boolean(data.work_order_id || data.process_id || data.assigned_to_mold_id);
      const assignmentData = {
        assigned_at: hasAssignment ? new Date().toISOString() : null,
        assigned_by: hasAssignment ? user?.id : null,
      };
      if (editingTag) {
        const { error } = await api
          .from('nfc_tags')
          .update({
            ...data,
            ...assignmentData,
          })
          .eq('id', editingTag.id);
        if (error) throw error;
      } else {
        const { error } = await api
          .from('nfc_tags')
          .upsert({
            ...data,
            ...assignmentData,
          }, { onConflict: 'tag_uid' });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nfc-tags'] });
      setIsDialogOpen(false);
      resetForm();
      toast.success(editingTag ? t.nfcTags?.updated || "Etiqueta actualizada" : t.nfcTags?.created || "Etiqueta registrada");
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api
        .from('nfc_tags')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nfc-tags'] });
      toast.success(t.nfcTags?.deleted || "Etiqueta eliminada");
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const [writeNfcAfterSave, setWriteNfcAfterSave] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.process_id && !formData.work_order_id) {
      toast.error("Selecciona una orden de trabajo para asignar un proceso");
      return;
    }

    const hasMoldAssignment = Boolean(formData.assigned_to_mold_id);
    const hasUserAssignment = Boolean(formData.assigned_to_user_id);
    const tagType = hasMoldAssignment
      ? "mold"
      : hasUserAssignment
      ? "user"
      : formData.process_id
      ? "process"
      : formData.work_order_id
      ? "work_order"
      : null;

    const payload = {
      tag_uid: formData.tag_uid,
      tag_label: formData.tag_label || null,
      process_id: (hasMoldAssignment || hasUserAssignment) ? null : (formData.process_id && formData.process_id !== "" ? formData.process_id : null),
      work_order_id: (hasMoldAssignment || hasUserAssignment) ? null : (formData.work_order_id && formData.work_order_id !== "" ? formData.work_order_id : null),
      assigned_to_mold_id: formData.assigned_to_mold_id && formData.assigned_to_mold_id !== "" ? formData.assigned_to_mold_id : null,
      assigned_to_user_id: formData.assigned_to_user_id && formData.assigned_to_user_id !== "" ? formData.assigned_to_user_id : null,
      location: formData.location || null,
      status: formData.status,
      notes: formData.notes || null,
      tag_type: tagType,
    };

    // If write NFC is enabled and we have a tag type, write after save
    if (writeNfcAfterSave && isSupported && tagType) {
      saveMutation.mutate(payload, {
        onSuccess: async () => {
          // Write to NFC tag
          const nfcPayload = {
            tagType,
            tagUid: formData.tag_uid,
            workOrderId: hasMoldAssignment ? undefined : (formData.work_order_id || undefined),
            processId: hasMoldAssignment ? undefined : (formData.process_id || undefined),
            moldId: hasMoldAssignment ? formData.assigned_to_mold_id : undefined,
          };
          await writeFullTag(formData.tag_uid, nfcPayload);
        },
      });
    } else {
      saveMutation.mutate(payload);
    }
  };

  const handleEdit = (tag: any) => {
    setEditingTag(tag);
    setFormData({
      tag_uid: tag.tag_uid,
      tag_label: tag.tag_label || "",
      process_id: tag.process_id || "",
      work_order_id: tag.work_order_id || "",
      assigned_to_mold_id: tag.assigned_to_mold_id || "",
      assigned_to_user_id: tag.assigned_to_user_id || "",
      location: tag.location || "",
      status: tag.status,
      notes: tag.notes || "",
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTag(null);
    setWriteNfcAfterSave(false);
    setFormData({
      tag_uid: "",
      tag_label: "",
      process_id: "",
      work_order_id: "",
      assigned_to_mold_id: "",
      assigned_to_user_id: "",
      location: "",
      status: "active",
      notes: "",
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="w-4 h-4 text-accent" />;
      case "inactive":
        return <XCircle className="w-4 h-4 text-muted-foreground" />;
      case "damaged":
        return <AlertCircle className="w-4 h-4 text-destructive" />;
      case "lost":
        return <AlertCircle className="w-4 h-4 text-destructive" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-accent/10 text-accent border-accent/20";
      case "inactive":
        return "bg-muted text-muted-foreground border-border";
      case "damaged":
        return "bg-destructive/10 text-destructive border-destructive/20";
      case "lost":
        return "bg-destructive/10 text-destructive border-destructive/20";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      active: t.nfcTags?.statusActive || "Activa",
      inactive: t.nfcTags?.statusInactive || "Inactiva",
      damaged: t.nfcTags?.statusDamaged || "Dañada",
      lost: t.nfcTags?.statusLost || "Perdida",
    };
    return labels[status] || status;
  };

  const stats = {
    total: tags?.length || 0,
    active: tags?.filter(t => t.status === 'active').length || 0,
    inactive: tags?.filter(t => t.status === 'inactive').length || 0,
    damaged: tags?.filter(t => t.status === 'damaged').length || 0,
  };

  const buildTagPayload = (tag: any) => {
    const tagType = tag.assigned_to_mold_id
      ? "mold"
      : tag.process_id
      ? "process"
      : tag.work_order_id
      ? "work_order"
      : null;

    if (!tagType) return null;

    return {
      tagType,
      workOrderId: tag.work_order_id || undefined,
      processId: tag.process_id || undefined,
      moldId: tag.assigned_to_mold_id || undefined,
      tagUid: tag.tag_uid,
    };
  };

  const handleWriteTag = async (tag: any) => {
    const payload = buildTagPayload(tag);
    if (!payload) {
      toast.error("Asigna una orden, proceso o molde antes de escribir NFC");
      return;
    }

    // Intentar USB primero si está disponible
    if (nfcBridge.isReaderReady) {
      toast.info("Escribiendo en chip NFC via USB...");
      const success = await writeTagUsb(payload);
      if (success) {
        toast.success("Chip NFC escrito correctamente");
      }
      return;
    }

    // Si no hay USB, intentar móvil
    if (isSupported) {
      await writeToTag(payload);
      return;
    }

    toast.error("NFC no disponible - conecta lector USB o usa dispositivo móvil");
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">
              {t.nfcTags?.title || "Administración de Etiquetas NFC"}
            </h2>
            <p className="text-muted-foreground mt-1">
              {t.nfcTags?.subtitle || "Gestiona el inventario de etiquetas NFC físicas"}
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                {t.nfcTags?.register || "Registrar Etiqueta"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingTag 
                    ? (t.nfcTags?.editTag || "Editar Etiqueta NFC")
                    : (t.nfcTags?.registerTag || "Registrar Nueva Etiqueta NFC")
                  }
                </DialogTitle>
                <DialogDescription>
                  {editingTag
                    ? (t.nfcTags?.editDescription || "Modifica la asignación y estado de la etiqueta")
                    : (t.nfcTags?.registerDescription || "Registra una nueva etiqueta NFC en el sistema")
                  }
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="tag_uid">{t.nfcTags?.tagUID || "UID de Etiqueta"} *</Label>
                    {!editingTag ? (
                      <>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <BarcodeInput
                              placeholder="Acerca el chip al lector USB o escribe el UID..."
                              onScan={(result) => {
                                // El lector Ewent envía el UID como texto
                                setFormData({ ...formData, tag_uid: result.code });
                                toast.success(`UID detectado: ${result.code}`);
                              }}
                              onError={(err) => toast.error(err)}
                              autoFocus
                              className="font-mono"
                            />
                          </div>
                          {isSupported && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={async () => {
                                setScanningForUid(true);
                                const result = await scanTagUid();
                                setScanningForUid(false);
                                if (result) {
                                  setFormData(prev => ({ ...prev, tag_uid: result.serialNumber }));
                                  if (result.isEmpty) {
                                    toast.info("Chip en blanco detectado - listo para grabar");
                                  }
                                }
                              }}
                              disabled={scanningForUid || isScanning}
                              title="Escanear con NFC del móvil"
                            >
                              {scanningForUid ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Scan className="w-4 h-4" />
                              )}
                              <span className="ml-2 hidden sm:inline">Móvil</span>
                            </Button>
                          )}
                        </div>
                        {formData.tag_uid && (
                          <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="font-mono text-sm text-green-700 dark:text-green-400">{formData.tag_uid}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="ml-auto h-6 px-2"
                              onClick={() => setFormData({ ...formData, tag_uid: "" })}
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground flex items-center gap-2">
                          {nfcBridge.isReaderReady ? (
                            <><span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse"></span> Lector USB listo - acerca el chip</>
                          ) : isSupported ? (
                            <>Usa el NFC del móvil o escribe manualmente</>
                          ) : (
                            <>Escribe el UID manualmente o conecta un lector USB</>
                          )}
                        </p>
                      </>
                    ) : (
                      <Input
                        id="tag_uid"
                        value={formData.tag_uid}
                        disabled
                        className="font-mono bg-muted"
                      />
                    )}
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="tag_label">{t.nfcTags?.tagLabel || "Etiqueta/Nombre"}</Label>
                    <Input
                      id="tag_label"
                      value={formData.tag_label}
                      onChange={(e) => setFormData({ ...formData, tag_label: e.target.value })}
                      placeholder="Ej: NFC-001 o Estación de Corte"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="work_order_id">{t.nfcTags?.workOrder || "Orden de Trabajo"}</Label>
                      <Select
                        value={formData.work_order_id || "none"}
                        onValueChange={(value) => setFormData({
                          ...formData,
                          work_order_id: value === "none" ? "" : value,
                          assigned_to_mold_id: value === "none" ? formData.assigned_to_mold_id : "",
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t.nfcTags?.selectWorkOrder || "Seleccionar..."} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t.nfcTags?.none || "Ninguna"}</SelectItem>
                          {workOrders?.map((wo) => (
                            <SelectItem key={wo.id} value={wo.id}>
                              {wo.order_number} - {wo.product_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="process_id">{t.nfcTags?.process || "Proceso"}</Label>
                      <Select
                        value={formData.process_id || "none"}
                        onValueChange={(value) => setFormData({
                          ...formData,
                          process_id: value === "none" ? "" : value,
                          assigned_to_mold_id: value === "none" ? formData.assigned_to_mold_id : "",
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t.nfcTags?.selectProcess || "Seleccionar..."} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t.nfcTags?.none || "Ninguno"}</SelectItem>
                          {processes?.map((proc) => (
                            <SelectItem key={proc.id} value={proc.id}>
                              {proc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="assigned_to_mold_id">Molde</Label>
                    <Select
                      value={formData.assigned_to_mold_id || "none"}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        assigned_to_mold_id: value === "none" ? "" : value,
                        work_order_id: value === "none" ? formData.work_order_id : "",
                        process_id: value === "none" ? formData.process_id : "",
                        assigned_to_user_id: value === "none" ? formData.assigned_to_user_id : "",
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Ninguno</SelectItem>
                        {molds?.map((mold) => (
                          <SelectItem key={mold.id} value={mold.id}>
                            {mold.name}{mold.code ? ` (${mold.code})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="assigned_to_user_id">Usuario (Tarjeta de Fichaje)</Label>
                    <Select
                      value={formData.assigned_to_user_id || "none"}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        assigned_to_user_id: value === "none" ? "" : value,
                        work_order_id: value === "none" ? formData.work_order_id : "",
                        process_id: value === "none" ? formData.process_id : "",
                        assigned_to_mold_id: value === "none" ? formData.assigned_to_mold_id : "",
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar usuario..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Ninguno</SelectItem>
                        {usersForAttendance?.map((u: any) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.full_name || u.employee_id || u.id.slice(0, 8)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Asignar para usar esta tarjeta NFC en fichaje de entrada/salida
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="location">{t.nfcTags?.location || "Ubicación"}</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        placeholder="Ej: Línea 1, Estación 3"
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="status">{t.nfcTags?.status || "Estado"} *</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">{t.nfcTags?.statusActive || "Activa"}</SelectItem>
                          <SelectItem value="inactive">{t.nfcTags?.statusInactive || "Inactiva"}</SelectItem>
                          <SelectItem value="damaged">{t.nfcTags?.statusDamaged || "Dañada"}</SelectItem>
                          <SelectItem value="lost">{t.nfcTags?.statusLost || "Perdida"}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="notes">{t.nfcTags?.notes || "Notas"}</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder={t.nfcTags?.notesPlaceholder || "Información adicional sobre la etiqueta..."}
                      rows={3}
                    />
                  </div>

                  {/* Option to write NFC after save */}
                  {isSupported && (formData.work_order_id || formData.process_id || formData.assigned_to_mold_id) && (
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <input
                        type="checkbox"
                        id="write_nfc"
                        checked={writeNfcAfterSave}
                        onChange={(e) => setWriteNfcAfterSave(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <div className="flex-1">
                        <Label htmlFor="write_nfc" className="flex items-center gap-2 cursor-pointer">
                          <Wifi className="w-4 h-4 text-primary" />
                          Grabar datos en el chip NFC
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Después de guardar, se te pedirá acercar el chip para grabarlo
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    {t.common?.cancel || "Cancelar"}
                  </Button>
                  <Button type="submit" disabled={saveMutation.isPending}>
                    {saveMutation.isPending ? (t.common?.saving || "Guardando...") : (t.common?.save || "Guardar")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.nfcTags?.totalTags || "Total"}</CardTitle>
              <Tag className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.nfcTags?.activeTags || "Activas"}</CardTitle>
              <CheckCircle className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-accent">{stats.active}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.nfcTags?.inactiveTags || "Inactivas"}</CardTitle>
              <XCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.inactive}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.nfcTags?.damagedTags || "Dañadas"}</CardTitle>
              <AlertCircle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{stats.damaged}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={t.nfcTags?.filterByStatus || "Filtrar por estado"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.nfcTags?.allStatus || "Todos"}</SelectItem>
              <SelectItem value="active">{t.nfcTags?.statusActive || "Activas"}</SelectItem>
              <SelectItem value="inactive">{t.nfcTags?.statusInactive || "Inactivas"}</SelectItem>
              <SelectItem value="damaged">{t.nfcTags?.statusDamaged || "Dañadas"}</SelectItem>
              <SelectItem value="lost">{t.nfcTags?.statusLost || "Perdidas"}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tags Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8 text-center text-muted-foreground">
                {t.common?.loading || "Cargando..."}
              </div>
            ) : tags && tags.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.nfcTags?.tagUID || "UID"}</TableHead>
                    <TableHead>{t.nfcTags?.tagLabel || "Etiqueta"}</TableHead>
                    <TableHead>{t.nfcTags?.assignment || "Asignación"}</TableHead>
                    <TableHead>{t.nfcTags?.location || "Ubicación"}</TableHead>
                    <TableHead>{t.nfcTags?.lastScan || "Último Escaneo"}</TableHead>
                    <TableHead>{t.nfcTags?.status || "Estado"}</TableHead>
                    <TableHead className="text-right">{t.common?.actions || "Acciones"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tags.map((tag) => (
                    <TableRow key={tag.id}>
                      <TableCell className="font-mono text-xs">{tag.tag_uid}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-primary" />
                          {tag.tag_label || <span className="text-muted-foreground italic">Sin etiqueta</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {tag.work_orders && (
                            <div className="flex items-center gap-2 text-sm">
                              <Package className="w-3 h-3 text-muted-foreground" />
                              <span>{tag.work_orders.order_number}</span>
                            </div>
                          )}
                          {tag.manufacturing_processes && (
                            <div className="text-xs text-muted-foreground">
                              {tag.manufacturing_processes.name}
                            </div>
                          )}
                          {tag.molds && (
                            <div className="text-xs text-muted-foreground">
                              Molde: {tag.molds.name}{tag.molds.code ? ` (${tag.molds.code})` : ""}
                            </div>
                          )}
                          {tag.user_profile && (
                            <div className="flex items-center gap-2 text-sm">
                              <Users className="w-3 h-3 text-blue-500" />
                              <span className="text-blue-600 font-medium">
                                {tag.user_profile.full_name || tag.user_profile.employee_id}
                              </span>
                              <Badge variant="outline" className="text-xs">Fichaje</Badge>
                            </div>
                          )}
                          {!tag.work_orders && !tag.manufacturing_processes && !tag.molds && !tag.user_profile && (
                            <span className="text-sm text-muted-foreground italic">Sin asignar</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {tag.location ? (
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="w-3 h-3 text-muted-foreground" />
                            {tag.location}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground italic">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {tag.last_scanned_at ? (
                          <div className="text-sm">
                            <div>{format(new Date(tag.last_scanned_at), "dd/MM/yyyy")}</div>
                            <div className="text-xs text-muted-foreground">
                              {(tag as any).scanned_by_profile?.full_name || t.nfcTags.unknown}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">Nunca</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getStatusColor(tag.status)}>
                          <span className="mr-1">{getStatusIcon(tag.status)}</span>
                          {getStatusLabel(tag.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleWriteTag(tag)}
                            disabled={!nfcAvailable}
                            title={nfcAvailable ? (nfcBridge.isReaderReady ? "Escribir NFC (USB)" : "Escribir NFC (Móvil)") : "NFC no disponible"}
                          >
                            <Radio className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => printLabel(tag)}
                            title={t.nfcTags?.printLabel || "Imprimir etiqueta"}
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(tag)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm(t.nfcTags?.confirmDelete || "¿Eliminar esta etiqueta?")) {
                                deleteMutation.mutate(tag.id);
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="p-12 text-center">
                <Tag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t.nfcTags?.noTags || "No hay etiquetas registradas"}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {t.nfcTags?.noTagsDesc || "Comienza registrando tu primera etiqueta NFC"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hidden printable label */}
        <div className="hidden">
          {printingTag && (
            <PrintableNFCLabel
              ref={printRef}
              data={{
                tagUid: printingTag.tag_uid,
                tagLabel: printingTag.tag_label,
                processName: printingTag.manufacturing_processes?.name,
                workOrderNumber: printingTag.work_orders?.order_number,
                productName: printingTag.work_orders?.product_name,
                location: printingTag.location,
                assignedAt: printingTag.assigned_at,
              }}
              companyName="Athenea NFC System"
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NFCTagsAdmin;
